import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/Layout/AuthLayout';

/**
 * SchoolAdminLoginPage - Login form for school administrators
 * Connects to backend /api/auth/school-admin/login endpoint
 */
const SchoolAdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { loginSchoolAdmin } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await loginSchoolAdmin(email, password);
      // Redirect to school admin dashboard on success
      navigate('/school-admin/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout showBackButton={true}>
      <form className="w-full" onSubmit={handleSubmit}>
        {/* Icon & Title */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-6">
            {/* Icon Glow */}
            <div className="absolute inset-0 blur-3xl opacity-40 bg-pink-500" />
            {/* Icon Container */}
            <div className="relative bg-gradient-to-br from-pink-500 to-rose-600 rounded-[1.5rem] p-6 shadow-2xl">
              <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
            School Admin Login
          </h2>
          <p className="text-gray-500 text-base">
            Manage your school's Brillabee platform
          </p>
        </div>

        {/* Form Fields */}
        <div className="space-y-5 mb-6">
          <div className="form-group">
            <label htmlFor="school-admin-email" className="block mb-2 font-semibold text-gray-800 text-sm">
              Email Address*
            </label>
            <input
              type="email"
              id="school-admin-email"
              placeholder="admin@school.edu"
              className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-pink-500 transition-colors"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="school-admin-password" className="block mb-2 font-semibold text-gray-800 text-sm">
              Password*
            </label>
            <input
              type="password"
              id="school-admin-password"
              placeholder="Enter password"
              className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-pink-500 transition-colors"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>
        </div>

        <button
          type="submit"
          className={`w-full bg-gradient-to-r from-pink-600 to-rose-700 text-white font-semibold py-4 px-6 rounded-2xl
                     hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 text-base tracking-wide mb-4 ${
            isLoading ? 'opacity-70 cursor-not-allowed' : ''
          }`}
          disabled={isLoading}
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </button>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm text-center">
            {error}
          </div>
        )}

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-400">New school?</span>
          </div>
        </div>

        <Link
          to="/register/school-admin"
          className="block text-center w-full bg-gray-100 text-gray-800 font-semibold py-4 px-6 rounded-2xl
                   hover:bg-gray-200 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 text-base"
        >
          Register with master code
        </Link>
      </form>
    </AuthLayout>
  );
};

export default SchoolAdminLoginPage;
